import { Link, useParams } from 'react-router-dom';
import { ArrowLeftRight } from '../../components/icons';
import Layout from '../../components/Layout';
import PageHeader from '../../components/PageHeader';
import Breadcrumb from '../../components/Breadcrumb';
import DownloadLink from '../../components/DownloadLink';
import NotFoundPage from '../NotFoundPage';
import { COLOR_COMPARISONS } from '../../data/colorComparisons';
import { hexToRgb, rgbToHsl } from '../../utils/colors';
import { getContrastRatio, getWCAGLevel } from '../../utils/colorAnalysis';
import { COMPARISON_WORKED_EXAMPLES, contrastPairUrl, resolveWorkedPair } from '../../data/colorWorkedExamples';

export default function ColorComparisonPage() {
  const { colors } = useParams<{ colors: string }>();
  const comparison = COLOR_COMPARISONS.find(item => item.slug === colors);
  if (!comparison) return <NotFoundPage />;

  const { first, second } = comparison;
  const swatches = [first, second].map(color => {
    const rgb = hexToRgb(color.hex)!;
    return {
      ...color,
      rgb,
      hsl: rgbToHsl(rgb.r, rgb.g, rgb.b),
      onWhite: getContrastRatio(color.hex, 'FFFFFF'),
      onBlack: getContrastRatio(color.hex, '000000'),
    };
  });
  const ratio = getContrastRatio(first.hex, second.hex);
  const identical = first.hex === second.hex;
  const related = COLOR_COMPARISONS.filter(item => comparison.relatedSlugs.includes(item.slug));
  const example = COMPARISON_WORKED_EXAMPLES[comparison.slug];
  const originalPair = example.options[example.fixOption];
  const corrected = resolveWorkedPair(originalPair, example.adjust);

  return (
    <Layout maxWidth="max-w-5xl">
      <Breadcrumb items={[{ label: 'Compare colors', to: '/compare' }, { label: `${first.name} vs ${second.name}` }]} />
      <article>
        <PageHeader icon={ArrowLeftRight} eyebrow="Color comparison guide" title={comparison.title} description={comparison.answer}>
          <p className="mt-3 text-xs text-ink-muted">By Color Match · Updated <time dateTime={comparison.updated}>September 14, 2026</time></p>
        </PageHeader>

        <div className="grid sm:grid-cols-2 gap-5">
          {swatches.map(color => (
            <section key={color.name} className="ui-panel overflow-hidden">
              <div className="h-36 sm:h-44" style={{ backgroundColor: `#${color.hex}` }} aria-hidden="true" />
              <div className="p-5">
                <h2 className="text-xl font-bold">{color.name}</h2>
                <p className="mt-1 font-mono text-lg">#{color.hex}</p>
                <p className="mt-3 text-sm leading-relaxed text-ink-secondary">{color.note}</p>
                <Link className="ui-button-secondary mt-4" to={`/color/${color.hex}`}>Explore #{color.hex}</Link>
              </div>
            </section>
          ))}
        </div>

        <section className="mt-8" aria-labelledby="comparison-values">
          <h2 id="comparison-values" className="text-2xl font-bold">Values and text contrast</h2>
          <div className="ui-panel mt-4 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <caption className="sr-only">Exact color values and calculated contrast against white and black</caption>
              <thead className="border-b border-surface-border bg-slate-50">
                <tr>
                  <th scope="col" className="p-4 font-semibold">Property</th>
                  {swatches.map(color => <th scope="col" key={color.name} className="p-4 font-semibold">{color.name}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                <tr><th scope="row" className="p-4 font-medium">HEX</th>{swatches.map(color => <td key={color.name} className="p-4 font-mono">#{color.hex}</td>)}</tr>
                <tr><th scope="row" className="p-4 font-medium">RGB</th>{swatches.map(color => <td key={color.name} className="p-4 font-mono">{color.rgb.r}, {color.rgb.g}, {color.rgb.b}</td>)}</tr>
                <tr><th scope="row" className="p-4 font-medium">HSL</th>{swatches.map(color => <td key={color.name} className="p-4 font-mono">{color.hsl.h}°, {color.hsl.s}%, {color.hsl.l}%</td>)}</tr>
                <tr>
                  <th scope="row" className="p-4 font-medium">Against white</th>
                  {swatches.map(color => <td key={color.name} className="p-4"><span className="font-mono">{color.onWhite.toFixed(3)}:1</span><span className="mt-1 block text-xs text-ink-secondary">Normal text: {getWCAGLevel(color.onWhite)}</span></td>)}
                </tr>
                <tr>
                  <th scope="row" className="p-4 font-medium">Against black</th>
                  {swatches.map(color => <td key={color.name} className="p-4"><span className="font-mono">{color.onBlack.toFixed(3)}:1</span><span className="mt-1 block text-xs text-ink-secondary">Normal text: {getWCAGLevel(color.onBlack)}</span></td>)}
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-sm leading-relaxed text-ink-secondary">Values use opaque sRGB colors. HSL is rounded for display and is not a perceptual color-difference score. Contrast classifications use the unrounded WCAG ratio.</p>
        </section>

        <section className="ui-panel mt-8 p-5 sm:p-6" aria-labelledby="pair-contrast">
          <h2 id="pair-contrast" className="text-2xl font-bold">{first.name} and {second.name} as text and background</h2>
          <p className="mt-3 text-ink-secondary">Pair contrast: <strong className="text-ink">{ratio.toFixed(3)}:1</strong>. Normal text: <strong>{getWCAGLevel(ratio)}</strong>. Large text: <strong>{getWCAGLevel(ratio, 'large')}</strong>.</p>
          {identical ? (
            <p className="mt-4 rounded-lg bg-slate-50 p-4 text-sm leading-relaxed">These are identical colors. Text in one on a background of the other is invisible at full opacity, with a contrast ratio of 1:1.</p>
          ) : (
            <div className="mt-5 grid sm:grid-cols-2 gap-4">
              {[[first, second], [second, first]].map(([foreground, background]) => (
                <figure key={foreground.name}>
                  <figcaption className="mb-2 text-sm text-ink-secondary">{foreground.name} text on {background.name}</figcaption>
                  <div className="rounded-lg p-6" style={{ color: `#${foreground.hex}`, backgroundColor: `#${background.hex}` }} aria-hidden="true">Aa — Sample text</div>
                </figure>
              ))}
            </div>
          )}
          <p className="mt-4 text-sm leading-relaxed text-ink-secondary">WCAG AA requires 4.5:1 for normal text and 3:1 for large text; AAA requires 7:1 and 4.5:1 respectively. Large text is at least 18 pt (24 CSS px), or 14 pt (about 18.67 CSS px) when bold. These checks cover text contrast, not complete accessibility.</p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link className="ui-button-primary" to={`/compare/${first.hex}-${second.hex}`}>Edit this color comparison</Link>
            <Link className="ui-button-secondary" to={`/contrast-checker?fg=${first.hex}&bg=${second.hex}`}>Check contrast and find a fix</Link>
          </div>
        </section>

        <section className="mt-9 space-y-5" aria-labelledby="worked-example">
          <div>
            <h2 id="worked-example" className="text-2xl font-bold">{example.title}</h2>
            <p className="mt-3 leading-relaxed text-ink-secondary">{example.context}</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            {example.options.map(option => {
              const optionRatio = getContrastRatio(option.foreground, option.background);
              return (
                <figure key={option.label} className="ui-panel p-5">
                  <figcaption className="text-sm font-semibold">{option.label}</figcaption>
                  <div className="mt-4 rounded-lg p-5 text-base font-semibold" style={{ color: option.foreground, backgroundColor: option.background }} aria-hidden="true">{example.sample}</div>
                  <p className="mt-3 text-sm font-semibold">{optionRatio.toFixed(3)}:1 — {optionRatio >= option.target ? 'Pass' : 'Fail'} normal text AA</p>
                  <p className="mt-2 text-sm leading-relaxed text-ink-secondary">{option.note}</p>
                  <Link className="mt-3 inline-block text-sm text-brand-700 underline underline-offset-4" to={contrastPairUrl(option.foreground, option.background)}>Check {option.foreground} on {option.background}</Link>
                </figure>
              );
            })}
          </div>
          <p className="leading-relaxed text-ink-secondary">{example.decision}</p>
          <div className="ui-panel p-5 sm:p-6">
            <h3 className="text-lg font-bold">A correction you can reproduce</h3>
            <p className="mt-2 text-sm leading-relaxed text-ink-secondary">Change the {example.adjust} while keeping the {example.adjust === 'foreground' ? 'background' : 'foreground'} fixed. The OKLCH search returns a nearby passing candidate, then measures the final six-digit sRGB colors again. It does not guarantee the mathematically nearest color.</p>
            <dl className="mt-4 grid sm:grid-cols-2 gap-4 text-sm">
              <div><dt className="font-semibold">Original pair</dt><dd className="mt-1 font-mono">{originalPair.foreground} on {originalPair.background}</dd><dd className="mt-1">{corrected.originalRatio.toFixed(3)}:1 — {corrected.originalRatio >= 4.5 ? 'Pass' : 'Fail'} normal text AA</dd></div>
              <div><dt className="font-semibold">Corrected pair</dt><dd className="mt-1 font-mono">{corrected.foreground} on {corrected.background}</dd><dd className="mt-1">{corrected.ratio.toFixed(3)}:1 — {corrected.meetsTarget ? 'Pass' : 'Fail'} normal text AA</dd></div>
            </dl>
            <div className="mt-5 rounded-lg p-5 text-base font-semibold" style={{ color: corrected.foreground, backgroundColor: corrected.background }} aria-hidden="true">{example.sample}</div>
            <pre className="mt-5 overflow-x-auto rounded-lg bg-surface-sunken p-4 text-xs leading-relaxed"><code>{`.example-label {\n  color: ${corrected.foreground};\n  background-color: ${corrected.background};\n}`}</code></pre>
            <p className="mt-2 text-xs text-ink-secondary">This snippet contains the checked color pair. Apply the non-color checks below when building the real component.</p>
            <div className="mt-4 flex flex-wrap gap-3">
              <Link className="ui-button-primary" to={contrastPairUrl(corrected.foreground, corrected.background)}>Recheck the corrected pair</Link>
              <Link className="ui-button-secondary" to={`/compare/${originalPair[example.adjust].slice(1)}-${corrected[example.adjust].slice(1)}`}>Compare original and corrected color</Link>
            </div>
          </div>
          <figure className="ui-panel overflow-hidden">
            <picture><source srcSet={`/images/compare/${comparison.slug}.webp`} type="image/webp" /><img src={`/images/compare/${comparison.slug}.png`} width={1200} height={720} loading="lazy" decoding="async" className="h-auto w-full" alt={`${first.name} and ${second.name} UI comparison: ${originalPair.label} changes from ${corrected.originalRatio.toFixed(3)}:1 to ${corrected.ratio.toFixed(3)}:1 with the corrected pair ${corrected.foreground} on ${corrected.background}.`} /></picture>
            <figcaption className="p-4 text-sm leading-relaxed text-ink-secondary">Original Color Match diagram for these exact sRGB inputs. The checked values and CSS are also available as text above. <DownloadLink className="text-brand-700 underline underline-offset-4" href={`/images/compare/${comparison.slug}.png`} tool="compare" action="download_image" format="png">Download PNG</DownloadLink>.</figcaption>
          </figure>
          <div>
            <h3 className="text-lg font-bold">Checks beyond the color pair</h3>
            <ul className="mt-3 list-disc pl-5 space-y-2 text-sm leading-relaxed text-ink-secondary">{example.nonColorChecks.map(check => <li key={check}>{check}</li>)}</ul>
            {comparison.slug === 'navy-blue-vs-royal-blue' && <p className="mt-3 text-sm"><Link className="text-brand-700 underline underline-offset-4" to={`/link-contrast-checker?link=${corrected.foreground.slice(1)}&text=FFFFFF&bg=000080&underline=always`}>Check this navy-panel link against both body text and background</Link>.</p>}
          </div>
        </section>

        <div className="mt-9 space-y-8">
          {comparison.sections.map(section => (
            <section key={section.heading}>
              <h2 className="text-xl font-bold">{section.heading}</h2>
              <p className="mt-3 leading-relaxed text-ink-secondary">{section.text}</p>
            </section>
          ))}
        </div>

        <section className="ui-panel mt-9 p-5 sm:p-6">
          <h2 className="text-lg font-bold">Sources and calculation method</h2>
          <p className="mt-2 text-sm leading-relaxed text-ink-secondary">CSS keyword values follow the W3C named-color table. A reference described as selected is an editorial swatch. Contrast is calculated from these exact values using WCAG relative luminance.</p>
          <ul className="mt-3 space-y-2 text-sm">
            <li><a className="text-brand-700 underline underline-offset-4" href="https://www.w3.org/TR/css-color-4/#named-colors">W3C CSS Color 4: named colors</a></li>
            <li><a className="text-brand-700 underline underline-offset-4" href="https://www.w3.org/WAI/WCAG22/Understanding/contrast-minimum.html">W3C WCAG 2.2: Contrast (Minimum)</a></li>
            <li><Link className="text-brand-700 underline underline-offset-4" to="/methodology">Color Match calculation methods and limitations</Link></li>
            <li><Link className="text-brand-700 underline underline-offset-4" to="/validation">Reproducible contrast and color-difference validation examples</Link></li>
          </ul>
        </section>

        <nav className="mt-9" aria-label="Related color comparisons">
          <h2 className="text-xl font-bold">Related color comparisons</h2>
          <ul className="mt-4 grid sm:grid-cols-2 gap-3">
            {related.map(item => <li key={item.slug}><Link className="ui-card-link h-full p-4 text-brand-700 font-medium" to={`/compare/${item.slug}`}>{item.first.name} vs {item.second.name}</Link></li>)}
          </ul>
        </nav>
      </article>
    </Layout>
  );
}
